import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Eye, EyeOff, Trash2, Download, Upload, LogOut, Users, Store, Calendar, AlertTriangle, RefreshCw, Shield, Key, Search } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  // Login form
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  
  // Stats
  const [stats, setStats] = useState(null);
  
  // Users management
  const [customers, setCustomers] = useState([]);
  const [shops, setShops] = useState([]);
  const [customerSearch, setCustomerSearch] = useState("");
  const [shopSearch, setShopSearch] = useState("");
  
  // Password dialog
  const [passwordDialog, setPasswordDialog] = useState(false);
  const [passwordTarget, setPasswordTarget] = useState(null); // {type: 'customer'|'shop', id, name}
  const [newPassword, setNewPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  
  // Dialogs
  const [deleteCustomersDialog, setDeleteCustomersDialog] = useState(false);
  const [deleteShopsDialog, setDeleteShopsDialog] = useState(false);
  const [importDialog, setImportDialog] = useState(false);
  
  // Import
  const [importFile, setImportFile] = useState(null);
  
  // Check if already logged in
  useEffect(() => {
    const savedToken = localStorage.getItem("admin_token");
    if (savedToken) {
      setToken(savedToken);
      setIsLoggedIn(true);
    }
  }, []);
  
  // Load stats when logged in
  useEffect(() => {
    if (isLoggedIn && token) {
      loadStats();
      loadCustomers();
      loadShops();
    }
  }, [isLoggedIn, token]);
  
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await fetch(`${API_URL}/api/admin/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || "Credenziali non valide");
      }
      
      localStorage.setItem("admin_token", data.token);
      setToken(data.token);
      setIsLoggedIn(true);
      toast.success("Accesso effettuato come Super Admin");
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };
  
  const handleLogout = () => {
    localStorage.removeItem("admin_token");
    setToken(null);
    setIsLoggedIn(false);
    setStats(null);
    toast.info("Disconnesso");
  };
  
  const loadStats = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          handleLogout();
          return;
        }
        // Try to get error message from response
        let errorMsg = "Errore nel caricamento statistiche";
        try {
          const errorData = await response.json();
          errorMsg = errorData.detail || errorMsg;
        } catch (e) {
          // Response is not JSON, use default message
        }
        throw new Error(errorMsg);
      }
      
      const data = await response.json();
      setStats(data);
    } catch (error) {
      console.error("Error loading stats:", error);
      toast.error(error.message);
    }
  };
  
  const loadCustomers = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/customers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          handleLogout();
          return;
        }
        throw new Error("Errore nel caricamento clienti");
      }
      
      const data = await response.json();
      setCustomers(data.customers || []);
    } catch (error) {
      console.error("Error loading customers:", error);
    }
  };
  
  const loadShops = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/shops`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          handleLogout();
          return;
        }
        throw new Error("Errore nel caricamento negozi");
      }
      
      const data = await response.json();
      setShops(data.shops || []);
    } catch (error) {
      console.error("Error loading shops:", error);
    }
  };
  
  const openPasswordDialog = (type, id, name) => {
    setPasswordTarget({ type, id, name });
    setNewPassword("");
    setShowNewPassword(false);
    setPasswordDialog(true);
  };
  
  const handleSetPassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast.error("La password deve essere di almeno 6 caratteri");
      return;
    }
    
    setLoading(true);
    try {
      const endpoint = passwordTarget.type === 'customer' 
        ? `${API_URL}/api/admin/customers/${passwordTarget.id}/password`
        : `${API_URL}/api/admin/shops/${passwordTarget.id}/password`;
      
      const response = await fetch(endpoint, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ new_password: newPassword })
      });
      
      if (!response.ok) throw new Error("Errore nell'aggiornamento password");
      
      toast.success(`Password di ${passwordTarget.name} aggiornata con successo`);
      setPasswordDialog(false);
      setPasswordTarget(null);
      setNewPassword("");
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };
  
  // Filtered lists
  const filteredCustomers = customers.filter(c => {
    const search = customerSearch.toLowerCase();
    return (
      (c.nome || "").toLowerCase().includes(search) ||
      (c.cognome || "").toLowerCase().includes(search) ||
      (c.email || "").toLowerCase().includes(search)
    );
  });
  
  const filteredShops = shops.filter(s => {
    const search = shopSearch.toLowerCase();
    return (
      (s.nome_negozio || "").toLowerCase().includes(search) ||
      (s.email || "").toLowerCase().includes(search)
    );
  });
  
  const handleDeleteCustomers = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/customers`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error("Errore nella cancellazione");
      
      const data = await response.json();
      toast.success(`Cancellati ${data.customers_deleted} clienti e ${data.bookings_deleted} prenotazioni`);
      setDeleteCustomersDialog(false);
      loadStats();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };
  
  const handleDeleteShops = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/shops`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error("Errore nella cancellazione");
      
      const data = await response.json();
      toast.success(`Cancellati ${data.shops_deleted} negozi`);
      setDeleteShopsDialog(false);
      loadStats();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };
  
  const handleExport = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/export`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error("Errore nell'esportazione");
      
      const data = await response.json();
      
      // Create and download JSON file
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `backup_niu_service_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      toast.success(`Esportati: ${data.customers.length} clienti, ${data.shops.length} negozi, ${data.bookings.length} prenotazioni`);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };
  
  const handleImportFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImportFile(file);
    }
  };
  
  const handleImport = async () => {
    if (!importFile) {
      toast.error("Seleziona un file di backup");
      return;
    }
    
    setLoading(true);
    try {
      const text = await importFile.text();
      const data = JSON.parse(text);
      
      // Validate backup structure
      if (!data.customers && !data.shops && !data.bookings) {
        throw new Error("File di backup non valido");
      }
      
      const response = await fetch(`${API_URL}/api/admin/import`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          customers: data.customers || [],
          shops: data.shops || [],
          bookings: data.bookings || [],
          settings: data.settings || []
        })
      });
      
      if (!response.ok) throw new Error("Errore nell'importazione");
      
      const result = await response.json();
      toast.success(`Importazione completata: ${result.results.customers_imported} clienti, ${result.results.shops_imported} negozi, ${result.results.bookings_imported} prenotazioni`);
      setImportDialog(false);
      setImportFile(null);
      loadStats();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };
  
  // Login form
  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-zinc-900 via-zinc-800 to-zinc-900 flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-zinc-900 border-zinc-700">
          <CardHeader className="text-center">
            <div className="mx-auto w-16 h-16 bg-red-600 rounded-full flex items-center justify-center mb-4">
              <Shield className="h-8 w-8 text-white" />
            </div>
            <CardTitle className="text-2xl text-white">Super Admin</CardTitle>
            <CardDescription className="text-zinc-400">
              Accesso riservato agli amministratori
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-zinc-300">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="admin@..."
                  required
                  className="bg-zinc-800 border-zinc-700 text-white"
                  data-testid="admin-email-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="password" className="text-zinc-300">Password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    required
                    className="bg-zinc-800 border-zinc-700 text-white pr-10"
                    data-testid="admin-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-200"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-red-600 hover:bg-red-700"
                data-testid="admin-login-btn"
              >
                {loading ? "Accesso..." : "Accedi"}
              </Button>
              
              <Button
                type="button"
                variant="ghost"
                onClick={() => navigate("/")}
                className="w-full text-zinc-400 hover:text-white"
              >
                Torna alla Home
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  // Admin Dashboard
  return (
    <div className="min-h-screen bg-gradient-to-br from-zinc-900 via-zinc-800 to-zinc-900">
      {/* Header */}
      <header className="bg-zinc-900/80 backdrop-blur-sm border-b border-zinc-700 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-red-600 rounded-full flex items-center justify-center">
              <Shield className="h-5 w-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-white">Super Admin</h1>
              <p className="text-sm text-zinc-400">Gestione Database</p>
            </div>
          </div>
          
          <Button
            variant="ghost"
            onClick={handleLogout}
            className="text-zinc-400 hover:text-white"
            data-testid="admin-logout-btn"
          >
            <LogOut className="h-4 w-4 mr-2" />
            Esci
          </Button>
        </div>
      </header>
      
      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card className="bg-zinc-900 border-zinc-700">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-blue-600/20 rounded-full flex items-center justify-center">
                  <Users className="h-6 w-6 text-blue-500" />
                </div>
                <div>
                  <p className="text-sm text-zinc-400">Clienti Registrati</p>
                  <p className="text-3xl font-bold text-white">{stats?.customers_count ?? "-"}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-zinc-900 border-zinc-700">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-green-600/20 rounded-full flex items-center justify-center">
                  <Store className="h-6 w-6 text-green-500" />
                </div>
                <div>
                  <p className="text-sm text-zinc-400">Negozi</p>
                  <p className="text-3xl font-bold text-white">{stats?.shops_count ?? "-"}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-zinc-900 border-zinc-700">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-orange-600/20 rounded-full flex items-center justify-center">
                  <Calendar className="h-6 w-6 text-orange-500" />
                </div>
                <div>
                  <p className="text-sm text-zinc-400">Prenotazioni Totali</p>
                  <p className="text-3xl font-bold text-white">{stats?.bookings_count ?? "-"}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
        
        {/* Tabs for management */}
        <Tabs defaultValue="overview" className="space-y-6">
          <TabsList className="bg-zinc-800 border-zinc-700">
            <TabsTrigger value="overview" className="data-[state=active]:bg-zinc-700">Panoramica</TabsTrigger>
            <TabsTrigger value="customers" className="data-[state=active]:bg-zinc-700">Clienti</TabsTrigger>
            <TabsTrigger value="shops" className="data-[state=active]:bg-zinc-700">Negozi</TabsTrigger>
            <TabsTrigger value="danger" className="data-[state=active]:bg-red-900/50 text-red-400">Zona Pericolosa</TabsTrigger>
          </TabsList>
          
          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            {/* Bookings by status */}
            {stats?.bookings_by_status && (
              <Card className="bg-zinc-900 border-zinc-700">
                <CardHeader>
                  <CardTitle className="text-white">Stato Prenotazioni</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-amber-500/10 rounded-lg p-4">
                      <p className="text-sm text-amber-400">In Attesa</p>
                      <p className="text-2xl font-bold text-white">{stats.bookings_by_status.pending_confirmation || 0}</p>
                    </div>
                    <div className="bg-green-500/10 rounded-lg p-4">
                      <p className="text-sm text-green-400">Confermati</p>
                      <p className="text-2xl font-bold text-white">{stats.bookings_by_status.confirmed || 0}</p>
                    </div>
                    <div className="bg-blue-500/10 rounded-lg p-4">
                      <p className="text-sm text-blue-400">Completati</p>
                      <p className="text-2xl font-bold text-white">{stats.bookings_by_status.completed || 0}</p>
                    </div>
                    <div className="bg-red-500/10 rounded-lg p-4">
                      <p className="text-sm text-red-400">Annullati</p>
                      <p className="text-2xl font-bold text-white">{stats.bookings_by_status.cancelled || 0}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
            
            {/* Export/Import */}
            <Card className="bg-zinc-900 border-zinc-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Download className="h-5 w-5 text-green-500" />
                  Backup Database
                </CardTitle>
                <CardDescription className="text-zinc-400">
                  Esporta o importa un backup completo del database
                </CardDescription>
              </CardHeader>
              <CardContent className="flex flex-col sm:flex-row gap-4">
                <Button
                  onClick={handleExport}
                  disabled={loading}
                  className="flex-1 bg-green-600 hover:bg-green-700"
                  data-testid="admin-export-btn"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Esporta Tutto (JSON)
                </Button>
                
                <Button
                  onClick={() => setImportDialog(true)}
                  disabled={loading}
                  variant="outline"
                  className="flex-1 border-zinc-600 text-zinc-300 hover:bg-zinc-800"
                  data-testid="admin-import-btn"
                >
                  <Upload className="h-4 w-4 mr-2" />
                  Importa Backup
                </Button>
              </CardContent>
            </Card>
          </TabsContent>
          
          {/* Customers Tab */}
          <TabsContent value="customers">
            <Card className="bg-zinc-900 border-zinc-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Users className="h-5 w-5 text-blue-500" />
                  Gestione Clienti ({customers.length})
                </CardTitle>
                <CardDescription className="text-zinc-400">
                  Modifica le password dei clienti registrati
                </CardDescription>
                <div className="relative mt-4">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
                  <Input
                    placeholder="Cerca cliente per nome o email..."
                    value={customerSearch}
                    onChange={(e) => setCustomerSearch(e.target.value)}
                    className="pl-10 bg-zinc-800 border-zinc-700 text-white"
                  />
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {filteredCustomers.length === 0 ? (
                    <p className="text-zinc-500 text-center py-4">Nessun cliente trovato</p>
                  ) : (
                    filteredCustomers.map((customer) => (
                      <div
                        key={customer.id}
                        className="flex items-center justify-between p-3 bg-zinc-800 rounded-lg hover:bg-zinc-750"
                      >
                        <div className="flex-1 min-w-0">
                          <p className="text-white font-medium truncate">
                            {customer.cognome} {customer.nome}
                          </p>
                          <p className="text-sm text-zinc-400 truncate">{customer.email}</p>
                          <p className="text-xs text-zinc-500">{customer.modello_moto}</p>
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openPasswordDialog('customer', customer.id, `${customer.cognome} ${customer.nome}`)}
                          className="ml-4 border-zinc-600 text-zinc-300 hover:bg-zinc-700"
                          data-testid={`set-password-customer-${customer.id}`}
                        >
                          <Key className="h-4 w-4 mr-1" />
                          Password
                        </Button>
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
          
          {/* Shops Tab */}
          <TabsContent value="shops">
            <Card className="bg-zinc-900 border-zinc-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Store className="h-5 w-5 text-green-500" />
                  Gestione Negozi ({shops.length})
                </CardTitle>
                <CardDescription className="text-zinc-400">
                  Modifica le password dei negozi registrati
                </CardDescription>
                <div className="relative mt-4">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
                  <Input
                    placeholder="Cerca negozio per nome o email..."
                    value={shopSearch}
                    onChange={(e) => setShopSearch(e.target.value)}
                    className="pl-10 bg-zinc-800 border-zinc-700 text-white"
                  />
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {filteredShops.length === 0 ? (
                    <p className="text-zinc-500 text-center py-4">Nessun negozio trovato</p>
                  ) : (
                    filteredShops.map((shop) => (
                      <div
                        key={shop.id}
                        className="flex items-center justify-between p-3 bg-zinc-800 rounded-lg hover:bg-zinc-750"
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="text-white font-medium truncate">
                              {shop.nome_negozio}
                            </p>
                            {shop.verified ? (
                              <span className="text-xs bg-green-600/20 text-green-400 px-2 py-0.5 rounded">Verificato</span>
                            ) : (
                              <span className="text-xs bg-amber-600/20 text-amber-400 px-2 py-0.5 rounded">Non verificato</span>
                            )}
                          </div>
                          <p className="text-sm text-zinc-400 truncate">{shop.email}</p>
                          <p className="text-xs text-zinc-500 truncate">{shop.indirizzo}</p>
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openPasswordDialog('shop', shop.id, shop.nome_negozio)}
                          className="ml-4 border-zinc-600 text-zinc-300 hover:bg-zinc-700"
                          data-testid={`set-password-shop-${shop.id}`}
                        >
                          <Key className="h-4 w-4 mr-1" />
                          Password
                        </Button>
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
          
          {/* Danger Zone Tab */}
          <TabsContent value="danger">
            <Card className="bg-zinc-900 border-red-900/50">
              <CardHeader>
                <CardTitle className="text-red-500 flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5" />
                  Zona Pericolosa
                </CardTitle>
                <CardDescription className="text-zinc-400">
                  Azioni irreversibili - procedi con cautela
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <Button
                  onClick={() => setDeleteCustomersDialog(true)}
                  disabled={loading}
                  variant="destructive"
                  className="w-full bg-red-600/20 hover:bg-red-600/40 text-red-400 border border-red-600/50"
                  data-testid="admin-delete-customers-btn"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Cancella Tutti i Clienti ({stats?.customers_count || 0})
                </Button>
                
                <Button
                  onClick={() => setDeleteShopsDialog(true)}
                  disabled={loading}
                  variant="destructive"
                  className="w-full bg-red-600/20 hover:bg-red-600/40 text-red-400 border border-red-600/50"
                  data-testid="admin-delete-shops-btn"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Cancella Tutti i Negozi ({stats?.shops_count || 0})
                </Button>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
        
        {/* Refresh button */}
        <div className="mt-8 text-center">
          <Button
            onClick={() => { loadStats(); loadCustomers(); loadShops(); }}
            variant="ghost"
            className="text-zinc-400 hover:text-white"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Aggiorna Dati
          </Button>
        </div>
      </main>
      
      {/* Delete Customers Dialog */}
      <Dialog open={deleteCustomersDialog} onOpenChange={setDeleteCustomersDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-700">
          <DialogHeader>
            <DialogTitle className="text-red-500 flex items-center gap-2">
              <AlertTriangle className="h-5 w-5" />
              Conferma Cancellazione
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              Stai per cancellare <strong className="text-white">TUTTI i clienti</strong> e le loro prenotazioni.
              Questa azione è <strong className="text-red-400">IRREVERSIBILE</strong>.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button
              variant="ghost"
              onClick={() => setDeleteCustomersDialog(false)}
              className="text-zinc-400"
            >
              Annulla
            </Button>
            <Button
              onClick={handleDeleteCustomers}
              disabled={loading}
              className="bg-red-600 hover:bg-red-700"
              data-testid="admin-confirm-delete-customers-btn"
            >
              {loading ? "Cancellazione..." : "Conferma Cancellazione"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Delete Shops Dialog */}
      <Dialog open={deleteShopsDialog} onOpenChange={setDeleteShopsDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-700">
          <DialogHeader>
            <DialogTitle className="text-red-500 flex items-center gap-2">
              <AlertTriangle className="h-5 w-5" />
              Conferma Cancellazione
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              Stai per cancellare <strong className="text-white">TUTTI i negozi</strong>.
              Questa azione è <strong className="text-red-400">IRREVERSIBILE</strong>.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button
              variant="ghost"
              onClick={() => setDeleteShopsDialog(false)}
              className="text-zinc-400"
            >
              Annulla
            </Button>
            <Button
              onClick={handleDeleteShops}
              disabled={loading}
              className="bg-red-600 hover:bg-red-700"
              data-testid="admin-confirm-delete-shops-btn"
            >
              {loading ? "Cancellazione..." : "Conferma Cancellazione"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Import Dialog */}
      <Dialog open={importDialog} onOpenChange={setImportDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-700">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Upload className="h-5 w-5 text-blue-500" />
              Importa Backup
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              Seleziona un file JSON di backup. <strong className="text-amber-400">ATTENZIONE:</strong> I dati esistenti verranno cancellati prima dell'importazione.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="border-2 border-dashed border-zinc-700 rounded-lg p-6 text-center">
              <input
                type="file"
                accept=".json"
                onChange={handleImportFileSelect}
                className="hidden"
                id="import-file"
                data-testid="admin-import-file-input"
              />
              <label
                htmlFor="import-file"
                className="cursor-pointer block"
              >
                <Upload className="h-8 w-8 mx-auto text-zinc-500 mb-2" />
                <p className="text-zinc-400">
                  {importFile ? (
                    <span className="text-green-400">{importFile.name}</span>
                  ) : (
                    "Clicca per selezionare un file"
                  )}
                </p>
              </label>
            </div>
            
            {importFile && (
              <p className="text-sm text-zinc-400 text-center">
                File selezionato: <span className="text-white">{importFile.name}</span> ({(importFile.size / 1024).toFixed(1)} KB)
              </p>
            )}
          </div>
          
          <DialogFooter className="gap-2">
            <Button
              variant="ghost"
              onClick={() => {
                setImportDialog(false);
                setImportFile(null);
              }}
              className="text-zinc-400"
            >
              Annulla
            </Button>
            <Button
              onClick={handleImport}
              disabled={loading || !importFile}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="admin-confirm-import-btn"
            >
              {loading ? "Importazione..." : "Importa"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Password Dialog */}
      <Dialog open={passwordDialog} onOpenChange={setPasswordDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-700">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Key className="h-5 w-5 text-blue-500" />
              Imposta Password
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              Imposta una nuova password per <strong className="text-white">{passwordTarget?.name}</strong>
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-zinc-300">Nuova Password</Label>
              <div className="relative">
                <Input
                  type={showNewPassword ? "text" : "password"}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Minimo 6 caratteri"
                  className="bg-zinc-800 border-zinc-700 text-white pr-10"
                  data-testid="admin-new-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-200"
                >
                  {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
          </div>
          
          <DialogFooter className="gap-2">
            <Button
              variant="ghost"
              onClick={() => {
                setPasswordDialog(false);
                setPasswordTarget(null);
                setNewPassword("");
              }}
              className="text-zinc-400"
            >
              Annulla
            </Button>
            <Button
              onClick={handleSetPassword}
              disabled={loading || !newPassword}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="admin-confirm-password-btn"
            >
              {loading ? "Salvataggio..." : "Salva Password"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
